import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { auth, db } from "./firebase";
import {
  collection,
  addDoc,
  query,
  orderBy,
  onSnapshot,
  serverTimestamp,
  doc,
  getDoc,
  getDocs,
  updateDoc,
} from "firebase/firestore";
import { Container, Form, Button, ListGroup, Image } from "react-bootstrap";
import { BsSend } from "react-icons/bs";

function ChatRoom() {
  const { friendId } = useParams();
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState("");
  const [user, setUser] = useState(null);
  const [friendName, setFriendName] = useState("相手");
  const [friendImage, setFriendImage] = useState("default-profile.png");

  useEffect(() => {
    const unsubscribeAuth = auth.onAuthStateChanged((currentUser) => {
      if (currentUser) {
        setUser(currentUser);
        fetchFriendInfo(friendId);
      }
    });
    return () => unsubscribeAuth();
  }, [friendId]);

  useEffect(() => {
    if (user) {
      return fetchMessages(user.uid, friendId);
    }
  }, [user, friendId]);

  useEffect(() => {
    if (user) {
      markMessagesAsRead();
    }
  }, [user, friendId, messages]);

  const fetchFriendInfo = async (friendId) => {
    try {
      const friendRef = doc(db, "users", friendId);
      const friendSnap = await getDoc(friendRef);
      if (friendSnap.exists()) {
        setFriendName(friendSnap.data().name || "USER");
        setFriendImage(friendSnap.data().profileImage || "default-profile.png");
      }
    } catch (error) {
      console.error("友達情報の取得エラー:", error);
    }
  };

  const fetchMessages = (userId, friendId) => {
    const chatId =
      userId < friendId ? `${userId}_${friendId}` : `${friendId}_${userId}`;
    const messagesRef = collection(db, "chats", chatId, "messages");
    const q = query(messagesRef, orderBy("timestamp"));

    const unsubscribe = onSnapshot(q, (snapshot) => {
      setMessages(
        snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
          timestamp: doc.data().timestamp
            ? doc.data().timestamp.toDate()
            : new Date(),
        }))
      );
    });

    return () => unsubscribe();
  };

  const markMessagesAsRead = async () => {
    if (!user) return;
    const chatId =
      user.uid < friendId
        ? `${user.uid}_${friendId}`
        : `${friendId}_${user.uid}`;
    const messagesRef = collection(db, "chats", chatId, "messages");
    const q = query(messagesRef, orderBy("timestamp"));

    const snapshot = await getDocs(q);
    snapshot.docs.forEach(async (doc) => {
      if (doc.data().sender !== user.uid && !doc.data().isRead) {
        await updateDoc(doc.ref, { isRead: true });
      }
    });
  };

  const sendMessage = async () => {
    if (!newMessage.trim()) return;
    if (!user) {
      console.error("ユーザーが未ログイン");
      return;
    }
    try {
      const chatId =
        user.uid < friendId
          ? `${user.uid}_${friendId}`
          : `${friendId}_${user.uid}`;
      const messagesRef = collection(db, "chats", chatId, "messages");
      await addDoc(messagesRef, {
        sender: user.uid,
        text: newMessage,
        timestamp: serverTimestamp(),
        isRead: false,
      });
      setNewMessage("");
    } catch (error) {
      console.error("メッセージ送信エラー:", error);
    }
  };

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return "";
    return timestamp.toLocaleString("ja-JP", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  return (
    <Container
      className="mt-4 p-4"
      style={{
        backgroundColor: "#f7f7f7",
        borderRadius: "15px",
        minHeight: "80vh",
      }}
    >
      <h4 className="mb-3 text-center text-dark" style={{ fontWeight: "bold" }}>
        {friendName}
      </h4>

      {/* メッセージリスト */}
      <ListGroup
        className="mb-3"
        style={{ maxHeight: "400px", overflowY: "auto", borderRadius: "10px" }}
      >
        {messages.map((msg) => (
          <ListGroup.Item
            key={msg.id}
            className={`d-flex align-items-center border-0 ${
              msg.sender === user?.uid
                ? "justify-content-end"
                : "justify-content-start"
            }`}
            style={{ backgroundColor: "transparent" }}
          >
            {/* 送信者のプロフィール画像 */}
            {msg.sender !== user?.uid && (
              <Image
                src={friendImage}
                roundedCircle
                width={40}
                height={40}
                className="me-2"
              />
            )}

            {/* メッセージ内容 */}
            <div>
              <div
                className={`p-2 rounded-pill ${
                  msg.sender === user?.uid
                    ? "bg-success text-white"
                    : "bg-light"
                }`}
                style={{ maxWidth: "60%", wordBreak: "break-word" }}
              >
                <p className="mb-0">{msg.text}</p>
              </div>
              <small
                className={`text-muted d-block mt-1 ${
                  msg.sender === user?.uid ? "text-end" : "text-start"
                }`}
              >
                {formatTimestamp(msg.timestamp)}{" "}
                {msg.sender === user?.uid && msg.isRead && "✔既読"}
              </small>
            </div>

            {/* 送信者が自分の場合 */}
            {msg.sender === user?.uid && (
              <Image
                src={user?.photoURL || "default-profile.png"}
                roundedCircle
                width={40}
                height={40}
                className="ms-2"
              />
            )}
          </ListGroup.Item>
        ))}
      </ListGroup>

      {/* メッセージ入力フォーム */}
      <Form className="d-flex">
        <Form.Control
          type="text"
          value={newMessage}
          onChange={(e) => setNewMessage(e.target.value)}
          placeholder="メッセージを入力..."
          className="rounded-pill me-2 p-2"
          style={{
            border: "1px solid #ccc",
            boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
          }}
        />
        <Button
          onClick={sendMessage}
          variant="primary"
          className="rounded-circle p-3"
          style={{
            backgroundColor: "#007bff",
            border: "none",
            boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
          }}
        >
          <BsSend size={20} />
        </Button>
      </Form>
    </Container>
  );
}

export default ChatRoom;
